import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth-context";
import { useCart } from "../lib/cart-context";
import PageSEO from "../components/SEO";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { register } = useAuth();
  const { refresh: refreshCart } = useCart();
  const navigate = useNavigate();

  const validate = () => {
    if (!name.trim()) return "Introduce tu nombre";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Email no válido";
    if (password.length < 8) return "La contraseña debe tener al menos 8 caracteres";
    if (password !== confirm) return "Las contraseñas no coinciden";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await register({ name: name.trim(), email: email.trim(), password });
      await refreshCart();
      toast.success("Cuenta creada. ¡Bienvenido/a a MercaGo!");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo crear la cuenta";
      setError(typeof msg === "string" ? msg : "Error al crear la cuenta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="register-page"
      className="mx-auto max-w-md px-6 py-16 sm:py-24"
    >
      <PageSEO
        title="Crear cuenta"
        description="Registrate en MercaGo para comprar productos halal desde España y enviarlos a tu familia en los campamentos saharauis."
        canonical="https://mercago.one/registro"
        noindex
      />
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center gap-2">
          <div className="h-10 w-10 rounded-xl bg-[#00953B] text-white grid place-items-center font-black">
            M
          </div>
          <span className="text-2xl font-black tracking-tight">
            Merca<span className="text-[#00953B]">Go</span>
          </span>
        </Link>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
        <h1 className="text-2xl font-black tracking-tight">Crear cuenta</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Regístrate y podrás enviar la compra a tu familia en los campamentos.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
          <div>
            <Label htmlFor="name" className="text-sm">Nombre</Label>
            <Input
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="register-name-input"
              className="mt-1 h-11"
              placeholder="Tu nombre"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-sm">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="mt-1 h-11"
              placeholder="tu@email.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-sm">Contraseña</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="mt-1 h-11"
              placeholder="Mínimo 8 caracteres"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-sm">Repite la contraseña</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="register-confirm-input"
              className="mt-1 h-11"
            />
          </div>
          {error && (
            <div
              data-testid="register-error"
              className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="w-full h-11 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white font-semibold"
          >
            {submitting ? "Creando cuenta…" : "Crear cuenta"}
          </Button>
        </form>
        <div className="mt-5 text-sm text-neutral-600 text-center">
          ¿Ya tienes cuenta?{" "}
          <Link
            to="/entrar"
            data-testid="register-to-login-link"
            className="text-[#00953B] font-semibold hover:underline"
          >
            Iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
}
